/* Designed sample day for Work Summary (new).
   Used when the URL has ?mock=1 — same /day + /day-brief shape the page
   already reads, so every chart and drawer path stays real. */

const DAY = "2026-08-28";

export const MOCK_DAY = DAY;

export const MOCK_MEMBERS = [
  { value: "mock-aanya", label: "Aanya Shah", sub: "Product Engineer" },
  { value: "mock-rohan", label: "Rohan Mehta", sub: "Design Lead" },
];

export function mockAt(h, m = 0) {
  return new Date(2026, 7, 28, h, m, 0, 0).getTime();
}

function blk({ from, to, app, title, cat, threadKey, src = "app", activity = 78, artifacts }) {
  const startMs = mockAt(...from);
  const endMs = mockAt(...to);
  const minutes = Math.round((endMs - startMs) / 60000);
  return {
    startMs, endMs, minutes, app, title: title || app, url: "", cat, src,
    activity,
    activityLabel: activity >= 70 ? "High" : activity >= 40 ? "Steady" : "Low",
    steadiness: activity >= 70 ? "Unbroken" : "Some breaks",
    threadKey: cat === "idle" ? null : threadKey,
    artifacts: artifacts || (title ? [title] : []),
  };
}

const THREADS = {
  checkout: "th-checkout",
  onboard: "th-onboard",
  pay: "th-pay",
  standup: "th-standup",
  release: "th-release",
};

const blocks = [
  blk({ from: [9, 14], to: [9, 55], app: "Cursor", title: "checkout-rewrite · useCart.ts", cat: "deep", threadKey: THREADS.checkout, activity: 86, artifacts: ["useCart.ts", "CheckoutForm.tsx"] }),
  blk({ from: [9, 55], to: [10, 40], app: "Slack", title: "eng-core · payment states", cat: "comms", threadKey: THREADS.standup, activity: 44, artifacts: ["#eng-core", "Aanya Shah"] }),
  blk({ from: [10, 40], to: [12, 10], app: "Cursor", title: "checkout-rewrite · OrderSummary", cat: "deep", threadKey: THREADS.checkout, activity: 81, artifacts: ["OrderSummary.tsx", "pricing.ts"] }),
  blk({ from: [12, 10], to: [13, 15], app: "Away", title: "Away from desk", cat: "idle", threadKey: null, activity: 0 }),
  blk({ from: [13, 15], to: [14, 0], app: "Google Chrome", title: "Razorpay — payment states", cat: "browse", threadKey: THREADS.pay, activity: 52, artifacts: ["docs.razorpay.com", "Failed payments"] }),
  blk({ from: [14, 0], to: [15, 48], app: "Figma", title: "Onboarding · v3 frames", cat: "review", threadKey: THREADS.onboard, activity: 64, artifacts: ["Onboarding v3", "Empty state"] }),
  blk({ from: [15, 48], to: [16, 5], app: "Slack", title: "design-crit · onboarding", cat: "comms", threadKey: THREADS.standup, activity: 48, artifacts: ["#design-crit"] }),
  blk({ from: [16, 5], to: [17, 40], app: "Cursor", title: "checkout-rewrite · retry path", cat: "deep", threadKey: THREADS.checkout, activity: 88, artifacts: ["retryPayment.ts", "CheckoutForm.tsx"] }),
  blk({ from: [17, 40], to: [18, 32], app: "Notion", title: "Release note · checkout", cat: "docs", threadKey: THREADS.release, activity: 61, artifacts: ["Release 28 Aug"] }),
];

const working = blocks.filter((b) => b.cat !== "idle");
const trackedMin = working.reduce((a, b) => a + b.minutes, 0);
const awayMin = blocks.filter((b) => b.cat === "idle").reduce((a, b) => a + b.minutes, 0);
const deepMin = working.filter((b) => ["deep", "docs", "review"].includes(b.cat)).reduce((a, b) => a + b.minutes, 0);

function thread({ key, title, cat, apps, outcomes, topics, interruptions = [], project = null }) {
  const mine = working.filter((b) => b.threadKey === key);
  const minutes = mine.reduce((a, b) => a + b.minutes, 0);
  return {
    key, title, cat, minutes, stretches: mine.length, project,
    apps, outcomes, topics, interruptions,
    activity: Math.round(mine.reduce((a, b) => a + b.activity * b.minutes, 0) / Math.max(1, minutes)),
    activityLabel: "High",
    artifacts: mine.flatMap((b) => b.artifacts).slice(0, 6),
    contentDepth: 12,
    hasDetail: false,
    firstMs: mine[0]?.startMs,
    lastMs: mine[mine.length - 1]?.endMs,
    spans: mine.map((b) => ({ startMs: b.startMs, endMs: b.endMs })),
  };
}

export const MOCK_DAY_DATA = {
  person: { id: "mock-aanya", name: "Aanya Shah", position: "Product Engineer" },
  date: DAY,
  source: "both",
  tz: { label: "Asia/Kolkata", offsetMin: 330 },
  coverage: { app: true, agent: true, appSpans: [], agentSpans: [] },
  bounds: { startMs: blocks[0].startMs, endMs: blocks[blocks.length - 1].endMs },
  blocks,
  threads: [
    thread({
      key: THREADS.checkout, title: "Shipping the checkout rewrite", cat: "deep",
      project: "Checkout",
      apps: ["Cursor"],
      outcomes: [{ text: "Retry path and cart totals landed in one sitting after lunch." }],
      topics: ["checkout", "retry", "cart"],
      interruptions: [
        { afterMs: mockAt(9, 55), gapMin: 45, cause: "Slack 45m", causeApps: ["Slack"] },
        { afterMs: mockAt(12, 10), gapMin: 235, cause: "lunch then Figma review", causeApps: ["Figma"] },
      ],
    }),
    thread({
      key: THREADS.onboard, title: "Reviewing onboarding v3", cat: "review",
      project: "Onboarding",
      apps: ["Figma"],
      outcomes: [{ text: "Empty state and first-run frames marked ready for build." }],
      topics: ["onboarding", "empty state"],
    }),
    thread({
      key: THREADS.release, title: "Writing the release note", cat: "docs",
      project: "Checkout",
      apps: ["Notion"],
      outcomes: [{ text: "Checkout release note drafted for tomorrow's ship." }],
      topics: ["release", "checkout"],
    }),
    thread({
      key: THREADS.pay, title: "Researching payment failure states", cat: "browse",
      project: "Checkout",
      apps: ["Google Chrome"],
      outcomes: [{ text: "Razorpay failed-payment states mapped to the retry path." }],
      topics: ["razorpay", "failures"],
    }),
    thread({
      key: THREADS.standup, title: "Standup and design crit", cat: "comms",
      project: "Team",
      apps: ["Slack"],
      outcomes: [{ text: "Payment-state questions unblocked the afternoon retry work." }],
      topics: ["standup", "design crit"],
    }),
  ],
  apps: [
    { name: "Cursor", minutes: working.filter((b) => b.app === "Cursor").reduce((a, b) => a + b.minutes, 0), cat: "deep" },
    { name: "Figma", minutes: working.filter((b) => b.app === "Figma").reduce((a, b) => a + b.minutes, 0), cat: "review" },
    { name: "Notion", minutes: working.filter((b) => b.app === "Notion").reduce((a, b) => a + b.minutes, 0), cat: "docs" },
    { name: "Google Chrome", minutes: working.filter((b) => b.app === "Google Chrome").reduce((a, b) => a + b.minutes, 0), cat: "browse" },
    { name: "Slack", minutes: working.filter((b) => b.app === "Slack").reduce((a, b) => a + b.minutes, 0), cat: "comms" },
  ],
  totals: {
    trackedMin,
    sessionMin: trackedMin + 12,
    deepMin,
    awayMin,
    offTaskMin: 0,
    focusPct: trackedMin ? Math.round((deepMin / trackedMin) * 100) : 0,
    longestBlockMin: Math.max(...working.map((b) => b.minutes)),
    switches: 8,
    switchesPerHour: 1.1,
    threadCount: 5,
    activity: 74,
    activityLabel: "High",
  },
};

export const MOCK_BRIEF = {
  takeaways: [
    {
      id: "tk-work", kind: "work",
      title: "The day belonged to the checkout rewrite",
      detail: "Three sittings in Cursor, longest after 4pm. Cart totals and the retry path both moved.",
      stat: "3h 46m", occupiesTime: true,
      shape: { minutes: 226, sessions: 3, windowFrom: mockAt(9, 14), windowTo: mockAt(17, 40) },
      anchors: [],
    },
    {
      id: "tk-win", kind: "win",
      title: "Afternoon sitting held for an hour and a half",
      detail: "16:05–17:40 with no context switch — the retry path landed in that stretch.",
      stat: "1h 35m unbroken", occupiesTime: true,
      shape: { minutes: 95, sessions: 1, windowFrom: mockAt(16, 5), windowTo: mockAt(17, 40) },
      anchors: [],
    },
    {
      id: "tk-risk", kind: "risk",
      title: "Checkout was picked up three times",
      detail: "Slack took 45 minutes out of the morning, then a long gap through lunch and review.",
      stat: "2 long breaks", occupiesTime: false,
      demonstration: [{ startMs: mockAt(9, 55) }, { startMs: mockAt(12, 10) }],
      anchors: [],
    },
    {
      id: "tk-pattern", kind: "pattern",
      title: "Research always lands right after lunch",
      detail: "Chrome docs filled the first 45 minutes back, then the day switched to review.",
      stat: "13:15–14:00", occupiesTime: true,
      shape: { minutes: 45, sessions: 1, windowFrom: mockAt(13, 15), windowTo: mockAt(14, 0) },
      anchors: [],
    },
  ],
  summary: "Aanya put 8h 13m on the board. The biggest share went to shipping the checkout rewrite, one of 5 threads. Longest unbroken run was 1h 48m.",
  running: false,
  error: null,
  empty: false,
};
